import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { usePharmacistsQuery, useSessionPharmacist } from '@/features/auth'
import { RecordModalBody } from '@/apps/legacy/components/templates/RecordModalBody'
import { useModal } from '@/apps/legacy/shared/hooks/useModal'
import {
  useDetachNSIPSMutation,
  useDrawerSelectionMode,
  isSelectableForGroup,
} from '@/features/yakureki'
import { useRegenerateFromAudioMutation } from '@/apps/legacy/features/yakureki/hooks/useRegenerateFromAudioMutation'
import { useRecordAudio } from '@/apps/legacy/features/yakureki/hooks/useRecordAudio'
import { GroupRecordingModal } from '@/apps/legacy/features/yakureki/components/GroupRecordingModal'
import { SoapLoadingToast } from '@/apps/legacy/features/yakureki/components/SoapLoadingToast'
import { PreRecordingScreen } from '@/apps/legacy/features/yakureki/PreRecordingScreen'
import { RecordingFailedAlert } from '@/apps/legacy/components/molecules/RecordingFailedAlert'
import { RecordingSubmitFailedError } from '@/features/yakureki/recording/streaming/apis/streaming'
import { PostRecordingScreen } from '@/apps/legacy/features/yakureki/PostRecordingScreen'
import { patchMarkDismissed } from '@/shared/apis/yakureki'
import { usePatientPageState } from '@/apps/legacy/features/patients/hooks/usePatientPageState'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import RefreshIcon from '@mui/icons-material/Refresh'
import MicIcon from '@mui/icons-material/Mic'
import { DrawerToggleButton } from '@/apps/legacy/components/molecules/DrawerToggleButton'
import { PatientInfoDisplay } from '@/apps/legacy/components/molecules/PatientInfoDisplay'
import { useMedicationUpdate } from '@/apps/legacy/shared/contexts/MedicationUpdateContext'
import { useToast } from '@/shared/contexts/ToastContext'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { getHeaderResponsiveStyles } from '@/shared/styles/responsive'
import { LAYOUT_CONSTANTS } from '@/shared/constants/layout'
import { MOBILE_HEADER_CONFIG } from '@/shared/constants/responsiveBreakpoints'
import EditNoteIcon from '@mui/icons-material/EditNote'

const IndexPage: FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { requestId } = useParams<{ requestId: string }>()
  const requestIdFromUrl = requestId

  // 統合フックで状態管理
  const {
    patientList,
    selectedPatient,
    patientDetail,
    screenMode,
    selectPatient,
    reloadPatientList,
    reloadPatientDetail,
  } = usePatientPageState(requestIdFromUrl)

  // Delete/Regenerate mutations (PostRecording Header から移動)
  const detachNSIPSMutation = useDetachNSIPSMutation()
  // 旧 path (= 音声 reprocess) 専用 hook.
  const regenerateSOAPMutation = useRegenerateFromAudioMutation()
  const [isDispatchDialogOpen, setIsDispatchDialogOpen] = useState(false)

  const handleDeleteClick = useCallback(() => {
    setIsDispatchDialogOpen(true)
  }, [])

  const handleDispatchDialogClose = useCallback(() => {
    setIsDispatchDialogOpen(false)
  }, [])

  const handleDetachNSIPS = useCallback(async () => {
    if (!selectedPatient) return
    await detachNSIPSMutation.mutateAsync(selectedPatient.id)
    setIsDispatchDialogOpen(false)
    reloadPatientDetail()
  }, [selectedPatient, detachNSIPSMutation, reloadPatientDetail])

  const handleRegenerateSOAP = useCallback(async () => {
    if (!selectedPatient) return
    try {
      await regenerateSOAPMutation.mutateAsync(selectedPatient.id)
      await reloadPatientDetail()
    } catch {
      // エラーハンドリングはMutation内で行われる
    }
  }, [selectedPatient, regenerateSOAPMutation, reloadPatientDetail])

  // Header 条件表示用の currentYakureki
  const currentYakureki = patientList.find((y) => y.id === selectedPatient?.id)

  // 薬剤更新コンテキストを使用
  useMedicationUpdate()

  const { showToast } = useToast()

  // 患者選択モード (まとめて完了 / まとめて録音 を一元管理)
  const selection = useDrawerSelectionMode()
  const [isGroupRecordingOpen, setIsGroupRecordingOpen] = useState(false)

  const handleGroupRecordingComplete = useCallback(() => {
    setIsGroupRecordingOpen(false)
    selection.exit()
    reloadPatientList()
  }, [selection, reloadPatientList])

  // まとめて完了の確定処理 (旧 Drawer.handleBatchDismiss を pages に引き上げ).
  // mode 管理を Drawer 外に出した以上、batch 実行も対称に外側で扱う方針.
  //
  // Promise.allSettled で部分失敗を握りつぶさず、成功/失敗件数をトーストで通知する.
  // 部分失敗時は成功分の DB 状態と selectedIds の整合維持が煩雑なため、一律で
  // mode を抜けて reload する. ユーザは失敗トーストを見て再度モードに入り直す.
  const handleConfirmBatchDismiss = useCallback(
    async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => patchMarkDismissed(id))
      )
      const failedCount = results.filter((r) => r.status === 'rejected').length
      const successCount = ids.length - failedCount

      selection.exit()
      await reloadPatientList()

      if (failedCount === 0) return

      showToast(
        successCount === 0
          ? `${failedCount}件すべての完了に失敗しました。再度お試しください。`
          : `${successCount}件完了しました。${failedCount}件は失敗したため再度ご確認ください。`,
        'error'
      )
    },
    [selection, reloadPatientList, showToast]
  )

  // NSIPS患者メモ（サイドパネル）
  const [isNsipsMemoOpen, setIsNsipsMemoOpen] = useState(false)

  // Toast通知関連のフック

  // 全体コピー用の選択状態判定
  // 指導キーフレーズ関連のフック
  const [name, setName] = useState('')
  const [memo, setMemo] = useState('')
  // 同伴/代理人有 (患者の家族が会話に参加するか) — 録音 start で SOAP 振り分け規則テーブルに反映
  const [hasCompanionOrSubstitute, setHasCompanionOrSubstitute] =
    useState(false)
  // 患者切替検知用 (React 推奨: prop 変化に応じた state 調整は render 中の
  // setState で行う — useEffect で setState を呼ぶ anti-pattern を避ける).
  const [lastPatientId, setLastPatientId] = useState<string | undefined>(
    undefined
  )
  if (lastPatientId !== patientDetail?.id) {
    setLastPatientId(patientDetail?.id)
    setHasCompanionOrSubstitute(
      patientDetail?.has_companion_or_substitute ?? false
    )
  }
  const { pharmacists: pharmacistData } = usePharmacistsQuery()
  const pharmacistList = useMemo(
    () => (pharmacistData?.pharmacists ?? []).filter((p) => p.is_own),
    [pharmacistData]
  )

  // 録音用セッション薬剤師（localStorage 永続 / stale ID 解決は hook 内）
  const { sessionPharmacistId, setSessionPharmacistId } =
    useSessionPharmacist(pharmacistList)

  // モーダル制御の関数を先に宣言（useModal より前に宣言する必要がある）
  const closeEditableModalRef = useRef<(() => void) | null>(null)

  const {
    isNewRecording,
    isTargetRecording,
    newAudioURL,
    targetAudioURL,
    newRecordButton,
    simpleTargetRecordButton,
    isSending,
    handleKeyDownForNewRecord,
    handleKeyDownForRecord,
    mediaRecorder,
    cancelNewRecording,
    startTargetRecording,
    streamingEngine,
  } = useRecordAudio(
    name,
    setName,
    () => closeEditableModalRef.current?.(),
    patientDetail?.id,
    sessionPharmacistId,
    memo,
    setMemo,
    hasCompanionOrSubstitute
  )

  // フリー録音モーダル close 時の cleanup:
  // ×ボタンや Esc で閉じたときに録音を確実に止める（患者切替ガードの副次発火防止）。
  const handleEditableModalClose = useCallback(() => {
    cancelNewRecording()
    setMemo('')
  }, [cancelNewRecording])

  const {
    openModal: openEditableModal,
    closeModal: closeEditableModal,
    renderModal: renderEditableModal,
    isOpen: isEditableModalOpen,
  } = useModal(
    <RecordModalBody
      name={name}
      setName={setName}
      isRecording={isTargetRecording}
      audioURL={newAudioURL}
      recordButton={newRecordButton}
      patientList={patientList}
      pharmacistId={sessionPharmacistId}
      setPharmacistId={setSessionPharmacistId}
      pharmacistList={pharmacistList}
      memo={memo}
      setMemo={setMemo}
    />,
    'center',
    handleEditableModalClose
  )

  // Ref に closeEditableModal を保存（useEffect内で行う）
  useEffect(() => {
    closeEditableModalRef.current = closeEditableModal
  }, [closeEditableModal])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableModalOpen) return
      if (event.key === 'Enter' && event.shiftKey) {
        // エンターキーが押された
        const isUseNsips = localStorageWrapper.getItem('IsUseNsips') || false
        if (!isUseNsips) {
          openEditableModal()
        }
      }
    }

    // ウィンドウ全体にイベントリスナーを追加
    window.addEventListener(
      'keydown',
      isEditableModalOpen ? handleKeyDownForNewRecord : handleKeyDown
    )
    if (!patientDetail?.audio) {
      window.addEventListener('keydown', handleKeyDownForRecord)
    }

    // コンポーネントのアンマウント時にイベントリスナーを削除
    return () => {
      window.removeEventListener(
        'keydown',
        isEditableModalOpen ? handleKeyDownForNewRecord : handleKeyDown
      )
      if (!patientDetail?.audio) {
        window.removeEventListener('keydown', handleKeyDownForRecord)
      }
    }
  }, [
    mediaRecorder,
    isNewRecording,
    isTargetRecording,
    newAudioURL,
    targetAudioURL,
    isEditableModalOpen,
    patientDetail,
    handleKeyDownForNewRecord,
    handleKeyDownForRecord,
    openEditableModal,
  ])

  // SOAP 生成中 / 完了 / 失敗の toast。patientList の各要素の status 遷移に
  // 応じて SoapLoadingToast が Snackbar を出す。legacy (batch) / streaming の
  // 両経路で共通 (mode gating なし).
  //
  // 選択中患者については patientDetail を渡して Toast 内で status 優先判定させる。
  // streaming 経路で patientList (delta sync) が PROCESSING を取りこぼす race の
  // 回避 (patientDetail は単一レコード snapshot で確実).
  const SoapLoadingToasts = (
    <>
      {patientList.map((yakureki, index) => (
        <SoapLoadingToast
          reloadPatientDetail={reloadPatientDetail}
          key={yakureki.id}
          yakureki={yakureki}
          patientDetail={
            patientDetail?.id === yakureki.id ? patientDetail : null
          }
          index={index}
          isSending={isSending}
        />
      ))}
    </>
  )

  // We've moved the selectAll function higher up to avoid code duplication

  const PortalNavigateButton = (
    <>
      <Button
        variant="outlined"
        sx={{
          width: '100%',
          whiteSpace: 'pre-wrap',
          color: colorTokens.blue[600],
          borderColor: colorTokens.blue[600],
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: 2,
          py: 1.5,
          '&:hover': {
            borderColor: colorTokens.blue[700],
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          },
          transition: 'all 0.2s ease',
        }}
        onClick={() => navigate('/portal')}
      >
        <DashboardIcon sx={{ fontSize: 20 }} />
        {'管理ポータルへ'}
      </Button>
    </>
  )

  // URLパラメータが変更された際に最後にアクセスしたIDを更新
  useEffect(() => {
    if (requestIdFromUrl && selectedPatient) {
      localStorageWrapper.setItem('lastAccessedRequestId', requestIdFromUrl)
    }
  }, [requestIdFromUrl, selectedPatient])

  // deep-link (= /yakureki/quick-record) 経由で history state.autoStart=true で
  // 着地した場合、 patientDetail が読み込まれて NSIPS pre-recording 表示が確定したら
  // 自動的に録音を開始する.
  // - URL クエリではなく history state を見ることで、 reload による二重発火を回避.
  // - 発火後は state を navigate replace で空に戻し、 次の手動操作と区別する.
  const location = useLocation()
  const autoStartFired = useRef(false)
  useEffect(() => {
    if (autoStartFired.current) return
    const state = location.state as { autoStart?: boolean } | null
    if (!state?.autoStart) return
    if (!patientDetail) return
    if (screenMode !== 'nsips-pre-recording') return
    if (isTargetRecording || isSending) return
    autoStartFired.current = true
    startTargetRecording()
    navigate(location.pathname, { replace: true, state: {} })
  }, [
    location.state,
    location.pathname,
    patientDetail,
    screenMode,
    isTargetRecording,
    isSending,
    startTargetRecording,
    navigate,
  ])

  const drawerBottomButtons = [PortalNavigateButton] as JSX.Element[]

  // 画面モードに応じたコンポーネントを表示
  const isRecording =
    isNewRecording ||
    isTargetRecording ||
    newAudioURL !== null ||
    targetAudioURL !== null

  // メモトグルボタン（pre-recording のみ表示、post-recording-confirmation では不要）
  const memoToggleButton =
    screenMode === 'nsips-pre-recording' ? (
      <Tooltip
        title={isNsipsMemoOpen ? 'メモを閉じる' : 'メモ表示'}
        placement="bottom"
      >
        <IconButton
          onClick={() => setIsNsipsMemoOpen((prev) => !prev)}
          sx={{
            color: isNsipsMemoOpen
              ? colorTokens.blue[600]
              : colorTokens.grey[500],
            '&:hover': {
              color: isNsipsMemoOpen
                ? colorTokens.blue[700]
                : colorTokens.grey[800],
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <EditNoteIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    ) : null

  // SharedHeader: screenMode switch の外に配置し、DrawerToggleButton の tree position を安定化
  const sharedHeader = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: colorTokens.background.paper,
        borderBottom: `1px solid ${colorTokens.grey[300]}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        py: 1,
        px: 2,
        minHeight: LAYOUT_CONSTANTS.headerMinHeight,
        [theme.breakpoints.down('sm')]: {
          minHeight: MOBILE_HEADER_CONFIG.minHeight,
          py: MOBILE_HEADER_CONFIG.paddingY,
        },
        zIndex: LAYOUT_CONSTANTS.zIndex.header,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={getHeaderResponsiveStyles(theme).container}>
        {/* 左: 患者メニューボタン */}
        <Box sx={getHeaderResponsiveStyles(theme).leftBox}>
          <DrawerToggleButton
            selectedPatient={selectedPatient}
            patientList={patientList}
            selectYakureki={selectPatient}
            BottomButtons={drawerBottomButtons}
            isRecording={isRecording}
            reloadPatientList={reloadPatientList}
            onRecordClick={openEditableModal}
            selection={selection}
            onStartGroupRecording={() => setIsGroupRecordingOpen(true)}
            onConfirmBatchDismiss={handleConfirmBatchDismiss}
            isSelectableForGroup={isSelectableForGroup}
            isPharmacistSelected={!!sessionPharmacistId}
          />
        </Box>

        {/* 1行目中央: 患者名（スマホ: 患者名のみ） */}
        <Box
          sx={{
            ...getHeaderResponsiveStyles(theme).centerBox,
            gap: 1.5,
          }}
        >
          {patientDetail &&
            screenMode !== 'non-nsips-pre-recording' &&
            screenMode !== 'loading' && (
              <PatientInfoDisplay
                patient={{
                  name: patientDetail.label,
                  gender: patientDetail.gender,
                  ...(patientDetail.age != null
                    ? { age: patientDetail.age }
                    : {}),
                }}
                variant="header"
              />
            )}
          {/* PC: マイク・薬剤師・メモトグルも同一行に表示 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
            }}
          >
            {screenMode === 'nsips-pre-recording' && (
              <>
                <Box
                  sx={{
                    minWidth: 120,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {simpleTargetRecordButton}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasCompanionOrSubstitute}
                      onChange={(e) =>
                        setHasCompanionOrSubstitute(e.target.checked)
                      }
                      disabled={isTargetRecording || isSending}
                      inputProps={{
                        'aria-label': '同伴/代理人有',
                      }}
                    />
                  }
                  label="同伴/代理人有"
                  sx={{ mr: 0 }}
                />
                {pharmacistList.length >= 1 && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="header-pharmacist-label">
                      担当薬剤師
                    </InputLabel>
                    <Select
                      labelId="header-pharmacist-label"
                      value={sessionPharmacistId}
                      label="担当薬剤師"
                      onChange={(e) => setSessionPharmacistId(e.target.value)}
                    >
                      {pharmacistList.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {screenMode === 'post-recording-confirmation' && patientDetail && (
              <>
                {patientDetail.has_companion_or_substitute && (
                  <Tooltip
                    title="録音時に「同伴/代理人有」が指定されました"
                    placement="bottom"
                  >
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        bgcolor: colorTokens.blue[50],
                        color: colorTokens.blue[700],
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      同伴/代理人有
                    </Box>
                  </Tooltip>
                )}
                {patientDetail.pharmacist_id &&
                  patientDetail.pharmacist_name && (
                    <FormControl size="small" sx={{ minWidth: 120 }} disabled>
                      <InputLabel id="header-pharmacist-label-readonly">
                        担当薬剤師
                      </InputLabel>
                      <Select
                        labelId="header-pharmacist-label-readonly"
                        value={patientDetail.pharmacist_id}
                        label="担当薬剤師"
                        IconComponent={() => null}
                      >
                        <MenuItem value={patientDetail.pharmacist_id}>
                          {patientDetail.pharmacist_name}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                {patientDetail.is_create_from_nsips &&
                  currentYakureki &&
                  currentYakureki.status !== 'processing' && (
                    <Tooltip title="録音削除" placement="bottom">
                      <IconButton
                        onClick={handleDeleteClick}
                        sx={{
                          color: colorTokens.grey[500],
                          p: 0.75,
                          '&:hover': {
                            color: 'warning.dark',
                            backgroundColor: 'rgba(230, 81, 0, 0.08)',
                          },
                        }}
                      >
                        <DeleteForeverIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                {patientDetail.status === 'failed' && (
                  <Tooltip title="SOAP再生成" placement="bottom">
                    <IconButton
                      onClick={handleRegenerateSOAP}
                      sx={{
                        color: colorTokens.grey[500],
                        p: 0.75,
                        '&:hover': {
                          color: colorTokens.blue[600],
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        },
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* PC右端: メモトグル */}
        <Box sx={getHeaderResponsiveStyles(theme).rightBox}>
          {memoToggleButton}
        </Box>

        {/* スマホ2行目: マイク・薬剤師・メモトグル */}
        <Box sx={getHeaderResponsiveStyles(theme).actionsRow}>
          {screenMode === 'nsips-pre-recording' && (
            <>
              {simpleTargetRecordButton}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={hasCompanionOrSubstitute}
                    onChange={(e) =>
                      setHasCompanionOrSubstitute(e.target.checked)
                    }
                    disabled={isTargetRecording || isSending}
                    inputProps={{
                      'aria-label': '同伴/代理人有',
                    }}
                  />
                }
                label="同伴/代理人有"
                sx={{ mr: 0 }}
              />
              {pharmacistList.length >= 1 && (
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="header-pharmacist-label-mobile">
                    担当薬剤師
                  </InputLabel>
                  <Select
                    labelId="header-pharmacist-label-mobile"
                    value={sessionPharmacistId}
                    label="担当薬剤師"
                    onChange={(e) => setSessionPharmacistId(e.target.value)}
                  >
                    {pharmacistList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {memoToggleButton}
            </>
          )}
          {screenMode === 'post-recording-confirmation' && patientDetail && (
            <>
              {patientDetail.pharmacist_id && patientDetail.pharmacist_name && (
                <FormControl size="small" sx={{ minWidth: 100 }} disabled>
                  <InputLabel id="header-pharmacist-label-mobile-readonly">
                    担当薬剤師
                  </InputLabel>
                  <Select
                    labelId="header-pharmacist-label-mobile-readonly"
                    value={patientDetail.pharmacist_id}
                    label="担当薬剤師"
                    IconComponent={() => null}
                  >
                    <MenuItem value={patientDetail.pharmacist_id}>
                      {patientDetail.pharmacist_name}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
              {patientDetail.is_create_from_nsips &&
                currentYakureki &&
                currentYakureki.status !== 'processing' && (
                  <Tooltip title="録音削除" placement="bottom">
                    <IconButton
                      onClick={handleDeleteClick}
                      sx={{
                        color: colorTokens.grey[500],
                        p: 0.75,
                        '&:hover': {
                          color: 'warning.dark',
                          backgroundColor: 'rgba(230, 81, 0, 0.08)',
                        },
                      }}
                    >
                      <DeleteForeverIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                )}
              {patientDetail.status === 'failed' && (
                <Tooltip title="SOAP再生成" placement="bottom">
                  <IconButton
                    onClick={handleRegenerateSOAP}
                    sx={{
                      color: colorTokens.grey[500],
                      p: 0.75,
                      '&:hover': {
                        color: colorTokens.blue[600],
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )

  // 削除確認ダイアログ（PostRecording から移動）
  const deleteConfirmDialog = (
    <Dialog open={isDispatchDialogOpen} onClose={handleDispatchDialogClose}>
      <DialogTitle>削除確認</DialogTitle>
      <DialogContent>
        本当この患者の録音・文字起こし・SOAPを削除しますか？
        <br />
        この操作は取り消せません。
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={6}
          pb={2}
        >
          <Box>
            <Button
              onClick={handleDispatchDialogClose}
              color="secondary"
              variant="outlined"
            >
              キャンセル
            </Button>
          </Box>
          <Box>
            <Button
              onClick={handleDetachNSIPS}
              color="error"
              autoFocus
              variant="outlined"
            >
              削除
            </Button>
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  )

  // 画面モードに応じたコンテンツを決定
  let content: React.ReactNode = null

  switch (screenMode) {
    case 'loading':
      content = (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          bgcolor={colorTokens.grey[100]}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography
            variant="h6"
            sx={{ mt: 3, color: colorTokens.text.secondary }}
          >
            読み込み中...
          </Typography>
        </Box>
      )
      break

    case 'non-nsips-pre-recording':
      content = (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            bgcolor={colorTokens.grey[100]}
            gap={2}
          >
            <MicIcon
              sx={{
                fontSize: 80,
                color: colorTokens.grey[400],
              }}
            />
            <Typography
              variant="body1"
              sx={{ color: colorTokens.text.secondary }}
            >
              患者メニューから録音を開始してください
            </Typography>
          </Box>
          {renderEditableModal()}
        </>
      )
      break

    case 'nsips-pre-recording':
      if (!patientDetail) break
      content = (
        <>
          {/* streaming finalize / fallback commit が失敗し blob を保持中の救済導線.
              finalizing 中は offline (= mutation paused で送信待ち) の時だけ出す.
              online finalizing は数秒で完走するので banner を出さない.
              interruption (= pendingBlob 無し) は対象外で自然に除外される. */}
          {(streamingEngine.state === 'failed' ||
            (streamingEngine.state === 'finalizing' &&
              streamingEngine.isOffline)) &&
            streamingEngine.hasPendingBlob && (
              <Box sx={{ px: 2, pt: 2 }}>
                <RecordingFailedAlert
                  errorMessage={streamingEngine.error?.message ?? ''}
                  hasPendingBlob={streamingEngine.hasPendingBlob}
                  onRetry={() => void streamingEngine.retry()}
                  onDownload={streamingEngine.downloadBlob}
                  retrying={streamingEngine.state === 'finalizing'}
                  isRetryHopeless={
                    streamingEngine.error instanceof
                      RecordingSubmitFailedError &&
                    streamingEngine.error.isRetryHopeless
                  }
                />
              </Box>
            )}
          <PreRecordingScreen
            patientDetail={patientDetail}
            patientList={patientList}
            renderEditableModal={renderEditableModal}
            pharmacistId={sessionPharmacistId}
            isMemoOpen={isNsipsMemoOpen}
          />
        </>
      )
      break

    case 'post-recording-confirmation':
      if (!patientDetail) break
      content = (
        <>
          <PostRecordingScreen
            selectedPatient={selectedPatient}
            patientList={patientList}
            patientDetail={patientDetail}
            renderEditableModal={renderEditableModal}
            reloadPatientDetail={reloadPatientDetail}
            onRegenerateSOAP={handleRegenerateSOAP}
          />
        </>
      )
      break
  }

  // ADR-055: SelectionProvider を削除（TanStack Query で選択状態を管理）
  return (
    <>
      {sharedHeader}
      {deleteConfirmDialog}
      {content}
      {SoapLoadingToasts}
      <GroupRecordingModal
        open={isGroupRecordingOpen}
        onClose={() => {
          setIsGroupRecordingOpen(false)
        }}
        selectedPatients={selection.getSelectedPatients(patientList)}
        selectedIds={Array.from(selection.selectedIds)}
        onComplete={handleGroupRecordingComplete}
        pharmacistId={sessionPharmacistId}
        setPharmacistId={setSessionPharmacistId}
        pharmacistList={pharmacistList}
      />
    </>
  )
}

// MedicationUpdateProvider は legacy shell でしか使われない (= SoapCheckBoxes +
// MedicationPlaceholderText の薬剤更新 hook). app entry に閉じ込めることで、
// App.tsx 側は legacy 廃止後に Provider を撤去する必要がない (= ε で apps/legacy/ ごと rm).
import { MedicationUpdateProvider } from '@/apps/legacy/shared/contexts/MedicationUpdateContext'
// ThemeProvider で canonical Notion theme を遮断し、 旧 kakeru theme を legacy shell 内に限定.
import { ThemeProvider } from '@mui/material/styles'
import { legacyTheme } from '@/apps/legacy/shared/styles/theme'

const LegacyIndexPage: FC = () => (
  <ThemeProvider theme={legacyTheme}>
    <MedicationUpdateProvider>
      <IndexPage />
    </MedicationUpdateProvider>
  </ThemeProvider>
)

export default LegacyIndexPage
